import { useEffect, useRef } from 'react';
import AgentAvatar from '../AgentAvatar';

export interface SessionSummary {
  id: string;
  agentName: string;
  agentDisplayName: string;
  phase: string;
  status: 'active' | 'completed';
  summary: string | null;
  stage: number | null;
  complete: boolean;
  messageCount: number;
  preview: string | null;
  createdAt: string;
  createdBy: string;
  creatorName: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  sessions: SessionSummary[];
  currentSessionId?: string;
  onSelectSession: (session: SessionSummary) => void;
  loading?: boolean;
}

const PHASE_LABELS: Record<string, string> = {
  phase_1_requirements:    'Phase 1 — Requirements',
  phase_2_architecture:    'Phase 2 — Architecture',
  phase_2_5_ux_design:     'Phase 2.5 — UX Design',
  phase_3_sprint_planning: 'Phase 3 — Sprint Planning',
  phase_4_development:     'Phase 4 — Development',
  phase_5_qa:              'Phase 5 — QA',
  phase_6_deployment:      'Phase 6 — Deployment',
  all:                     'Cross-Phase',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function SessionHistoryDrawer({
  open,
  onClose,
  sessions,
  currentSessionId,
  onSelectSession,
  loading,
}: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Group sessions by phase
  const grouped = new Map<string, SessionSummary[]>();
  for (const s of sessions) {
    const key = s.phase;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(s);
  }

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 transition-opacity"
          style={{ background: 'rgba(0,0,0,0.3)' }}
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-200 ease-out"
        style={{
          width: 380,
          maxWidth: '90vw',
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxShadow: open ? '-4px 0 24px rgba(0,0,0,0.12)' : 'none',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
            Session History
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-alt)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            aria-label="Close history"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3" style={{ scrollbarWidth: 'thin' }}>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <span className="inline-block w-5 h-5 rounded-full border-2 animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--primary)' }} />
            </div>
          )}

          {!loading && sessions.length === 0 && (
            <div className="text-center py-12">
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                No sessions yet
              </p>
            </div>
          )}

          {!loading && [...grouped.entries()].map(([phase, phaseSessions]) => (
            <div key={phase} className="mb-4">
              {/* Phase group label */}
              <p
                className="px-1 py-1.5 mb-1.5"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {PHASE_LABELS[phase] ?? phase}
              </p>

              {phaseSessions.map(s => {
                const isCurrent = s.id === currentSessionId;
                const isActive = s.status === 'active';

                return (
                  <button
                    key={s.id}
                    onClick={() => onSelectSession(s)}
                    disabled={isCurrent}
                    className="w-full text-left rounded-lg p-3 mb-1.5 transition-all"
                    style={{
                      background: isCurrent ? 'rgba(124,58,237,0.08)' : 'transparent',
                      border: isCurrent ? '1px solid rgba(124,58,237,0.2)' : '1px solid transparent',
                      cursor: isCurrent ? 'default' : 'pointer',
                      opacity: isCurrent ? 1 : undefined,
                    }}
                    onMouseEnter={e => {
                      if (!isCurrent) e.currentTarget.style.background = 'var(--surface-alt)';
                    }}
                    onMouseLeave={e => {
                      if (!isCurrent) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="flex-shrink-0 mt-0.5">
                        <AgentAvatar agentType={s.agentName} size={28} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className="font-medium truncate"
                            style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}
                          >
                            {s.agentDisplayName}
                          </span>
                          {/* Status badge */}
                          <span
                            className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                            style={
                              isActive
                                ? { background: 'rgba(16,185,129,0.12)', color: 'var(--success, #10b981)' }
                                : { background: 'var(--surface-alt)', color: 'var(--text-secondary)' }
                            }
                          >
                            {isActive ? 'Active' : 'Done'}
                          </span>
                          {isCurrent && (
                            <span
                              className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                              style={{ background: 'rgba(124,58,237,0.12)', color: 'var(--primary)' }}
                            >
                              Current
                            </span>
                          )}
                        </div>

                        {/* Preview */}
                        {s.preview && (
                          <p
                            className="mt-0.5 truncate"
                            style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}
                          >
                            {s.preview}
                          </p>
                        )}

                        {/* Meta row */}
                        <div className="flex items-center gap-3 mt-1">
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)' }}>
                            {formatDate(s.createdAt)}
                          </span>
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)' }}>
                            {s.messageCount} msg{s.messageCount !== 1 ? 's' : ''}
                          </span>
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)' }}>
                            {s.creatorName}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
